/**
 * Turning the cycle payload into grid rows - pure, so the maths can be tested
 * without rendering 200 rows, and so 5B's cell editing changes one function
 * rather than the component tree.
 */
import { draftErrors, toNumber } from '../draft'
import type { DraftErrors, DraftItemLine, DraftOrder } from '../draft'
import { WEIGHT_SERVICE_SLUG } from './billingServices'
import type {
  B2bCycleResponse,
  B2bOrderDraft,
  B2bOrderRecord,
  B2bRoomRecord,
} from '../../../services/b2bApi'

export type BillingRowStatus = 'pending' | 'in_progress' | 'done'

export interface BillingRoomRow {
  roomId: string
  roomNumber: string
  block: string | null
  status: BillingRowStatus
  /** Wash & Iron. null when the room has no weight entry. */
  weightKg: number | null
  ratePerKg: number | null
  pieces: number | null
  dcItemCount: number
  shoeItemCount: number
  /** Per-service money, for the footer's subtotal breakout (brief §11). */
  weightAmount: number
  dcAmount: number
  shoeAmount: number
  total: number
  /**
   * Every saved service in this room has been through a tag run (brief §27).
   *
   * ALL of them, not any: a room whose Wash & Iron printed this morning and
   * whose Dry Cleaning was added since is not "printed", and a tick claiming
   * otherwise would send the operator to the sorting table with three untagged
   * garments.
   */
  tagsPrinted: boolean
  /** The most recent run, for the marker's tooltip. */
  tagsPrintedAt: string | null
}

const DRY_CLEANING = 'dry-cleaning'
const SHOE_CARE = 'shoe-care'

/**
 * A room's status is DERIVED, never stored. `b2b_rooms` deliberately carries no
 * per-cycle column: a room is done when it has a saved order in this cycle, so
 * nothing has to be reset across 200 rows when an invoice closes the cycle.
 *
 * `dirtyRoomIds` is how 5B will mark a room the operator has touched but not
 * yet saved; until then no room is ever "In Progress", which is correct - an
 * unsaved edit is the only thing that state can mean.
 */
export const buildRows = (
  rooms: B2bRoomRecord[],
  orders: B2bOrderRecord[],
  dirtyRoomIds: ReadonlySet<string> = new Set(),
): BillingRoomRow[] => {
  const byRoom = new Map<string, B2bOrderRecord[]>()
  for (const order of orders) {
    const bucket = byRoom.get(order.roomId)
    if (bucket) bucket.push(order)
    else byRoom.set(order.roomId, [order])
  }

  return rooms.map((room) => {
    const roomOrders = byRoom.get(room.id) ?? []
    const weightOrder = roomOrders.find((order) => order.serviceSlug === WEIGHT_SERVICE_SLUG)
    const dcOrder = roomOrders.find((order) => order.serviceSlug === DRY_CLEANING)
    const shoeOrder = roomOrders.find((order) => order.serviceSlug === SHOE_CARE)
    const weightLine = weightOrder?.items[0]

    const status: BillingRowStatus = dirtyRoomIds.has(room.id)
      ? 'in_progress'
      : roomOrders.length > 0
        ? 'done'
        : 'pending'

    return {
      roomId: room.id,
      roomNumber: room.room_number,
      block: room.block,
      status,
      weightKg: weightLine?.weightKg ?? null,
      ratePerKg: weightLine?.unitPrice ?? null,
      pieces: weightOrder?.pieceCount ?? null,
      dcItemCount: dcOrder?.items.length ?? 0,
      shoeItemCount: shoeOrder?.items.length ?? 0,
      weightAmount: weightOrder?.amount ?? 0,
      dcAmount: dcOrder?.amount ?? 0,
      shoeAmount: shoeOrder?.amount ?? 0,
      total: roomOrders.reduce((sum, order) => sum + order.amount, 0),
      tagsPrinted: roomOrders.length > 0 && roomOrders.every((order) => Boolean(order.printedAt)),
      tagsPrintedAt: roomOrders.reduce<string | null>(
        (latest, order) =>
          order.printedAt && (!latest || order.printedAt > latest) ? order.printedAt : latest,
        null,
      ),
    }
  })
}

export interface VisibleTotals {
  weight: number
  dc: number
  shoe: number
  total: number
  rooms: number
}

/**
 * The footer's numbers.
 *
 * Computed from the rows PASSED IN, which are the filtered ones - the footer
 * says "Visible totals" and must mean it. A footer that quietly reported the
 * whole cycle while the operator looked at a filtered view would be worse than
 * no footer: it would look like an answer to the question on screen.
 */
export const visibleTotals = (rows: BillingRoomRow[]): VisibleTotals =>
  rows.reduce<VisibleTotals>(
    (acc, row) => ({
      weight: acc.weight + row.weightAmount,
      dc: acc.dc + row.dcAmount,
      shoe: acc.shoe + row.shoeAmount,
      total: acc.total + row.total,
      rooms: acc.rooms + 1,
    }),
    { weight: 0, dc: 0, shoe: 0, total: 0, rooms: 0 },
  )

/** Rows matching the toolbar's search box and status segment. */
export const filterRows = (
  rows: BillingRoomRow[],
  query: string,
  status: 'all' | BillingRowStatus,
): BillingRoomRow[] => {
  const needle = query.trim().toLowerCase()
  return rows.filter((row) => {
    if (status !== 'all' && row.status !== status) return false
    if (!needle) return true
    return row.roomNumber.toLowerCase().includes(needle)
  })
}

/** Everything the grid needs from one cycle response. */
export const rowsFromCycle = (
  cycle: Pick<B2bCycleResponse, 'rooms' | 'orders'>,
  dirtyRoomIds?: ReadonlySet<string>,
): BillingRoomRow[] => buildRows(cycle.rooms, cycle.orders, dirtyRoomIds)


/* ────────────────────────────────────────────────────────────────────────
 * In-progress edits
 *
 * The grid's Wash & Iron cells are a `DraftOrder` per room - the same shape
 * the rest of the module already uses, so `draftErrors` validates the grid
 * without a second set of rules. Numbers stay STRINGS: an operator typing
 * "3." must not have it become 3 (or NaN) between two keystrokes.
 * ──────────────────────────────────────────────────────────────────────── */

/** A room's unsaved Wash & Iron entry. */
export type RoomDraft = DraftOrder

export const emptyDraft = (roomId: string, ratePerKg: number | null): RoomDraft => ({
  id: `draft-${roomId}`,
  serviceSlug: WEIGHT_SERVICE_SLUG,
  weightKg: '',
  // Pre-filled from the client's negotiated card, and freely overridable -
  // FRD §8: the card is a default, never a rule. Room 101 at ₹129 and Room 102
  // at ₹125 must both be possible (brief §8).
  ratePerKg: ratePerKg === null ? '' : String(ratePerKg),
  pieces: '',
  items: [],
})

/** The draft a saved row starts from when the operator re-opens it. */
export const draftFromRow = (row: BillingRoomRow, fallbackRate: number | null): RoomDraft => ({
  id: `draft-${row.roomId}`,
  serviceSlug: WEIGHT_SERVICE_SLUG,
  weightKg: row.weightKg === null ? '' : String(row.weightKg),
  ratePerKg:
    row.ratePerKg === null ? (fallbackRate === null ? '' : String(fallbackRate)) : String(row.ratePerKg),
  pieces: row.pieces === null || row.pieces === 0 ? '' : String(row.pieces),
  items: [],
})

/**
 * Money for a weight entry, at 2dp.
 *
 * Deliberately NOT `draftAmount` from draft.ts, which does
 * `Math.round(weight × rate)`. The brief's own worked example is
 * "5.2 kg × ₹129/kg = ₹670.80", and the server stores 670.80 - rounding to
 * ₹671 here would put a different number in front of the operator than the one
 * on the client's invoice, and across 200 rooms those half-rupees compound into
 * a visible gap. draft.ts is left untouched; this is the grid's money rule.
 */
export const draftWeightAmount = (draft: RoomDraft): number =>
  Number((toNumber(draft.weightKg) * toNumber(draft.ratePerKg)).toFixed(2))

/** True once the operator has put anything in the row. */
export const draftIsEmpty = (draft: RoomDraft): boolean =>
  !draft.weightKg.trim() && !draft.pieces.trim()

/**
 * Errors that BLOCK a save.
 *
 * `draftErrors` also flags a missing piece count, but that must not stop an
 * operator saving: weighing forty bags and counting them afterwards is a normal
 * way to work. A missing count is shown as a hint and blocks PRINTING instead,
 * where it would otherwise produce tags that lie about what is in the bag.
 */
export const blockingErrors = (draft: RoomDraft): DraftErrors => {
  if (draftIsEmpty(draft)) return {}
  const { pieces, ...rest } = draftErrors(draft)
  void pieces
  return rest
}

export const hasBlockingErrors = (draft: RoomDraft): boolean =>
  Object.keys(blockingErrors(draft)).length > 0

/** What a row shows, once any unsaved edit is laid over the saved data. */
export interface RowDisplay {
  weightKg: string
  ratePerKg: string
  pieces: string
  weightAmount: number
  dcItemCount: number
  shoeItemCount: number
  dcAmount: number
  shoeAmount: number
  total: number
  status: BillingRowStatus
  /** Suppressed while the row is dirty - see rowDisplay. */
  tagsPrinted: boolean
  tagsPrintedAt: string | null
}

export const rowDisplay = (row: BillingRoomRow, entry?: RoomEntry): RowDisplay => {
  if (!entry) {
    return {
      weightKg: row.weightKg === null ? '' : String(row.weightKg),
      ratePerKg: row.ratePerKg === null ? '' : String(row.ratePerKg),
      pieces: row.pieces === null || row.pieces === 0 ? '' : String(row.pieces),
      weightAmount: row.weightAmount,
      dcItemCount: row.dcItemCount,
      shoeItemCount: row.shoeItemCount,
      dcAmount: row.dcAmount,
      shoeAmount: row.shoeAmount,
      total: row.total,
      status: row.status,
      tagsPrinted: row.tagsPrinted,
      tagsPrintedAt: row.tagsPrintedAt,
    }
  }

  const weightAmount = draftWeightAmount(entry.weight)
  // Services this entry has not touched keep whatever is already saved.
  const dc = entryItemsAmount(entry, DRY_CLEANING) ?? row.dcAmount
  const shoe = entryItemsAmount(entry, SHOE_CARE) ?? row.shoeAmount

  return {
    weightKg: entry.weight.weightKg,
    ratePerKg: entry.weight.ratePerKg,
    pieces: entry.weight.pieces,
    weightAmount,
    dcItemCount: entryItemCount(entry, DRY_CLEANING) ?? row.dcItemCount,
    shoeItemCount: entryItemCount(entry, SHOE_CARE) ?? row.shoeItemCount,
    dcAmount: dc,
    shoeAmount: shoe,
    total: Number((weightAmount + dc + shoe).toFixed(2)),
    status: 'in_progress',
    // An unsaved edit makes whatever was printed stale - the tags on the
    // garments no longer describe what this room is being billed for. Claiming
    // "printed" here would hide exactly the room that needs reprinting.
    tagsPrinted: false,
    tagsPrintedAt: null,
  }
}

/** Footer totals with unsaved edits applied - what the operator is looking at. */
export const totalsWithDrafts = (
  rows: BillingRoomRow[],
  drafts: ReadonlyMap<string, RoomEntry>,
): VisibleTotals =>
  rows.reduce<VisibleTotals>(
    (acc, row) => {
      const display = rowDisplay(row, drafts.get(row.roomId))
      return {
        weight: Number((acc.weight + display.weightAmount).toFixed(2)),
        dc: Number((acc.dc + display.dcAmount).toFixed(2)),
        shoe: Number((acc.shoe + display.shoeAmount).toFixed(2)),
        total: Number((acc.total + display.total).toFixed(2)),
        rooms: acc.rooms + 1,
      }
    },
    { weight: 0, dc: 0, shoe: 0, total: 0, rooms: 0 },
  )

/** Status filtering has to see the unsaved status, not the saved one. */
export const filterRowsWithDrafts = (
  rows: BillingRoomRow[],
  drafts: ReadonlyMap<string, RoomEntry>,
  query: string,
  status: 'all' | BillingRowStatus,
): BillingRoomRow[] => {
  const needle = query.trim().toLowerCase()
  return rows.filter((row) => {
    if (status !== 'all' && rowDisplay(row, drafts.get(row.roomId)).status !== status) return false
    if (!needle) return true
    return row.roomNumber.toLowerCase().includes(needle)
  })
}


/* ────────────────────────────────────────────────────────────────────────
 * A room's whole unsaved entry
 *
 * FRD §8: "A room can carry multiple services in the same cycle." So the unit
 * of unsaved work is the ROOM, not one service - the weight entry the grid
 * edits inline, plus the garment lines each item-based service holds.
 * ──────────────────────────────────────────────────────────────────────── */

export interface RoomEntry {
  weight: RoomDraft
  /** Keyed by service slug: 'dry-cleaning', 'shoe-care'. */
  items: Record<string, DraftItemLine[]>
}

export const emptyRoomEntry = (roomId: string, ratePerKg: number | null): RoomEntry => ({
  weight: emptyDraft(roomId, ratePerKg),
  items: {},
})

/** Seeded from what is already saved, so re-opening a room never loses it. */
export const roomEntryFromRow = (row: BillingRoomRow, fallbackRate: number | null): RoomEntry => ({
  weight: draftFromRow(row, fallbackRate),
  items: {},
})

const itemsTotal = (lines: DraftItemLine[] = []): number =>
  Number(
    lines.reduce((sum, line) => sum + toNumber(line.quantity) * toNumber(line.unitPrice), 0).toFixed(2),
  )

/** Money for one item-based service in this entry, or null when untouched. */
export const entryItemsAmount = (entry: RoomEntry | undefined, slug: string): number | null =>
  entry?.items[slug] ? itemsTotal(entry.items[slug]) : null

export const entryItemCount = (entry: RoomEntry | undefined, slug: string): number | null =>
  entry?.items[slug] ? entry.items[slug].length : null

/** Has the operator put anything in this room at all? */
export const entryIsEmpty = (entry: RoomEntry): boolean =>
  draftIsEmpty(entry.weight) && Object.values(entry.items).every((lines) => lines.length === 0)

/**
 * Errors that block a save, across the whole room.
 *
 * The weight side reuses `blockingErrors`; the item side applies FRD §8's rule
 * that a garment needs a whole-number quantity and a manually entered price.
 */
export const entryHasBlockingErrors = (entry: RoomEntry): boolean => {
  if (hasBlockingErrors(entry.weight)) return true
  return Object.values(entry.items).some((lines) =>
    lines.some((line) => {
      const quantity = toNumber(line.quantity)
      return !Number.isInteger(quantity) || quantity < 1 || toNumber(line.unitPrice) <= 0
    }),
  )
}

/** The room's total with every unsaved service applied. */
export const entryRoomTotal = (row: BillingRoomRow, entry: RoomEntry | undefined): number => {
  if (!entry) return row.total
  const weight = draftWeightAmount(entry.weight)
  const dc = entryItemsAmount(entry, DRY_CLEANING) ?? row.dcAmount
  const shoe = entryItemsAmount(entry, SHOE_CARE) ?? row.shoeAmount
  return Number((weight + dc + shoe).toFixed(2))
}


/**
 * A room entry as the save endpoint wants it.
 *
 * Only services the operator actually filled in are sent: the endpoint REPLACES
 * a room's services, so including an empty one would delete work that is
 * already saved. Numbers travel as the strings they were typed as - the server
 * is the authority on parsing them, and re-formatting here would be a second
 * opinion about what "3." means.
 */
export const entryToPayload = (entry: RoomEntry): B2bOrderDraft[] => {
  const orders: B2bOrderDraft[] = []

  if (entry.weight.weightKg.trim()) {
    orders.push({
      serviceSlug: WEIGHT_SERVICE_SLUG,
      weightKg: entry.weight.weightKg,
      ratePerKg: entry.weight.ratePerKg,
      pieces: entry.weight.pieces,
    })
  }

  for (const [slug, lines] of Object.entries(entry.items)) {
    if (!lines || lines.length === 0) continue
    orders.push({
      serviceSlug: slug,
      items: lines.map((line) => ({
        itemName: line.itemName,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
    })
  }

  return orders
}
